import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Sparkles, TreeDeciduous } from 'lucide-react';

const LoginPage = () => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    grade: 'Grade 2',
    age: 7
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.password, formData.grade, formData.age);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'age' ? parseInt(value) || 0 : value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' }}>
      {/* Floating decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-6xl animate-float opacity-30">🌿</div>
        <div className="absolute top-40 right-20 text-5xl animate-float opacity-30" style={{ animationDelay: '1s' }}>🦋</div>
        <div className="absolute bottom-40 left-20 text-4xl animate-float opacity-30" style={{ animationDelay: '0.5s' }}>🌸</div>
        <div className="absolute bottom-20 right-10 text-5xl animate-float opacity-30" style={{ animationDelay: '1.5s' }}>🍃</div>
      </div>
      
      <div className="w-full max-w-md relative">
        {/* Logo and title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img 
              src="/Asy_transfer_logo.png" 
              alt="Logo" 
              style={{
                width: '100px',
                height: '100px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h1 className="text-4xl font-bold text-emerald-800" style={{ fontFamily: 'Fredoka' }}>
            Asymmetrical Learning
          </h1>
          <p className="text-emerald-600 mt-2 flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" />
            Where reading adventures begin
            <TreeDeciduous className="w-4 h-4" />
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-emerald-100">
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              data-testid="login-tab"
              className={`flex-1 py-3 rounded-2xl font-bold text-lg transition-all ${
                isLogin 
                  ? 'bg-emerald-500 text-white shadow-lg' 
                  : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
              }`}
            >
              Sign In
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-2xl mb-4 text-center font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-emerald-700 font-bold mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  data-testid="name-input"
                  placeholder="What should we call you?"
                  className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none text-lg transition-all"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <label className="block text-emerald-700 font-bold mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                data-testid="email-input"
                placeholder="your@email.com"
                className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none text-lg transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-emerald-700 font-bold mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                data-testid="password-input"
                placeholder="Your secret code"
                className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none text-lg transition-all"
                required
              />
            </div>

            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-emerald-700 font-bold mb-2">Grade</label>
                  <select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    data-testid="grade-select"
                    className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none text-lg transition-all bg-white"
                  >
                    <option value="Kindergarten">Kindergarten</option>
                    <option value="Grade 1">Grade 1</option>
                    <option value="Grade 2">Grade 2</option>
                    <option value="Grade 3">Grade 3</option>
                  </select>
                </div>
                <div>
                  <label className="block text-emerald-700 font-bold mb-2">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    data-testid="age-input"
                    min="4"
                    max="12"
                    className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none text-lg transition-all"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="w-full py-4 rounded-2xl font-bold text-xl text-white transition-all btn-bounce disabled:opacity-50"
              style={{ 
                background: 'linear-gradient(135deg, #10B981, #059669)',
                boxShadow: '0 4px 0 #047857'
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">🌸</span>
                  Loading...
                </span>
              ) : isLogin ? (
                'Start Reading! 📚'
              ) : (
                'Create My Account! 🌟'
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
